// Conteo de inventario: un producto (y, si aplica, una compañía) a la vez.
//
// Todo el lote inicial (10 productos, hasta 2 pasos cada uno: Master/GTech) se
// renderiza de una sola carga desde el servidor, oculto salvo la primera card.
// No hay ida y vuelta al servidor para "el siguiente producto": ya está en el
// DOM. Esto reemplaza el flujo viejo de `count.js.erb`/`nearby_products.js.erb`
// (SJR + alert() + zoom CSS) por fetch/JSON + toasts + un solo botón por vez.
//
// Los pasos de una card NO se recorren con un índice que sólo avanza: se
// mantiene una lista de pasos PENDIENTES. Así "contar en la otra compañía"
// reordena (el paso que se deja atrás vuelve después) en vez de descartar, y
// "saltar" descarta el producto entero de forma explícita.
import { post, get } from "./api"
import { toast } from "./toast"

const COMPANY_LABELS = { master: "Master", gt: "GTech" }

function companyLabel(company) {
  return COMPANY_LABELS[company] || company
}

export function initCounts() {
  const screen = document.getElementById("count-screen")
  if (!screen) return

  const queueEl = screen.querySelector("[data-count-queue]")
  const saveBtn = document.querySelector("[data-count-save]")
  const skipBtn = document.querySelector("[data-count-skip]")
  const finishBtn = document.querySelector("[data-count-finish]")
  const doneEl = screen.querySelector("[data-count-done]")
  const moreBtn = screen.querySelector("[data-count-more]")
  const progressLabel = screen.querySelector("[data-progress-label]")
  const progressFill = screen.querySelector("[data-progress-fill]")
  if (!queueEl || !saveBtn) return

  const queue = Array.from(queueEl.querySelectorAll("[data-count-card]"))
  const excludedIds = queue.map((card) => card.dataset.productId)
  let totalProducts = queue.length
  let doneProducts = 0
  let skippedProducts = 0

  // Pasos de la card visible (índices dentro de su `data-steps`).
  //
  // Se distinguen dos clases, y ésa es la regla de negocio: los pasos SIN `warn`
  // son compañías donde el sistema registra unidades — son los que hay que
  // contar, el flujo normal. Los `warn` son compañías sin inventario
  // registrado: sólo directores/supervisores pueden forzarlos, y NO entran en
  // la secuencia; quedan disponibles a demanda con «Contar en …». Antes todos
  // los pasos eran obligatorios, así que a un director el aviso de "solo
  // directores/supervisores" le salía en casi todos los productos.
  let currentStep = null
  const countedSteps = new Set()

  function currentCard() {
    return queue[0] || null
  }

  function stepsOf(card) {
    try {
      return JSON.parse(card.dataset.steps || "[]")
    } catch (_e) {
      return []
    }
  }

  // Compañías con unidades registradas que todavía no se contaron: lo que el
  // flujo normal exige.
  function requiredStepsOf(card) {
    return stepsOf(card)
      .map((step, i) => (step.warn ? null : i))
      .filter((i) => i !== null && !countedSteps.has(i))
  }

  // Cualquier paso al que se pueda saltar a mano (incluye los `warn`).
  function switchableStepsOf(card) {
    return stepsOf(card)
      .map((_step, i) => i)
      .filter((i) => i !== currentStep && !countedSteps.has(i))
  }

  function updateProgress() {
    const processed = doneProducts + skippedProducts
    const shown = Math.min(processed + 1, totalProducts)
    // El contador vive en el header, al lado del título: se mantiene corto
    // ("3 de 10") porque ahí no hay ancho para una frase.
    if (progressLabel) progressLabel.textContent = totalProducts > 0 ? `${shown} de ${totalProducts}` : ""
    if (progressFill) {
      progressFill.style.width = totalProducts > 0 ? `${Math.min(100, (processed / totalProducts) * 100)}%` : "0%"
    }
  }

  function renderStep(card) {
    const steps = stepsOf(card)
    const step = steps[currentStep]
    if (!step) {
      finishCard()
      return
    }

    const label = companyLabel(step.company)
    const badge = card.querySelector("[data-step-badge]")
    const stepLabel = card.querySelector("[data-step-label]")
    const warning = card.querySelector("[data-step-warning]")
    const qtyInput = card.querySelector("[data-count-qty]")
    const switchBtn = card.querySelector("[data-switch-company]")

    if (badge) {
      badge.textContent = label
      // El color de la banda dice de qué compañía se está contando de un
      // vistazo: naranja Master, azul oscuro GTech.
      badge.classList.toggle("m-badge--master", step.company === "master")
      badge.classList.toggle("m-badge--gt", step.company === "gt")
    }
    if (stepLabel) stepLabel.textContent = `Cantidad en ${label}`
    if (warning) warning.style.display = step.warn ? "inline-flex" : "none"
    if (qtyInput) {
      qtyInput.value = ""
      // Foco SÍNCRONO, y con la card anterior todavía en el DOM (ver
      // `finishCard`). En un teléfono el teclado sólo se mantiene abierto si el
      // foco pasa de un campo a otro sin quedar en el aire ni un instante. Con
      // el `setTimeout` de antes el teclado alcanzaba a cerrarse y, como para
      // entonces ya estábamos fuera del gesto del usuario (la respuesta del
      // guardado llega después de un `await`), Android se negaba a reabrirlo:
      // había que tocar el campo de nuevo en CADA producto. Eso es lo que se
      // sentía como "el guardado se demora unos segundos".
      qtyInput.focus()
    }

    // El botón de cambio alcanza cualquier paso no contado — incluidos los
    // `warn`, que es como un director llega a una compañía sin stock cuando de
    // verdad lo necesita, sin que se le imponga en cada producto.
    const otherStep = switchableStepsOf(card)[0]
    if (switchBtn) {
      if (otherStep === undefined) {
        switchBtn.hidden = true
      } else {
        switchBtn.hidden = false
        const otro = steps[otherStep]
        switchBtn.textContent = otro.warn
          ? `Contar en ${companyLabel(otro.company)} (sin stock)`
          : `Contar en ${companyLabel(otro.company)}`
      }
    }

    updateSaveButtonState()
  }

  function updateSaveButtonState() {
    const card = currentCard()
    if (!card) return
    const qtyInput = card.querySelector("[data-count-qty]")
    saveBtn.disabled = !qtyInput || qtyInput.value.trim() === ""
  }

  function showCard(card) {
    if (!card) return
    countedSteps.clear()

    // Se propone contar donde hay unidades. Si el producto no tiene stock en
    // ninguna compañía, se ofrece el primer paso `warn` (sólo existe para
    // directores/supervisores) en vez de saltarlo en silencio: ese es el caso
    // raro en que el aviso tiene sentido.
    const required = requiredStepsOf(card)
    currentStep = required.length > 0 ? required[0] : stepsOf(card).findIndex((s) => s.warn)
    if (currentStep === -1) currentStep = undefined

    card.hidden = false
    renderStep(card)
    preloadNextImage()
  }

  // La imagen de una card oculta NO se descarga: `loading="lazy"` dentro de un
  // elemento `hidden` no pide nada hasta que se muestra. Resultado: la foto del
  // producto siguiente empezaba a bajar justo al guardar, compitiendo por el
  // ancho de banda con la petición del conteo (en el flujo viejo, con los 10
  // formularios visibles, todas bajaban al abrir la pantalla). Se adelanta la
  // descarga de la próxima mientras la persona cuenta la actual.
  function preloadNextImage() {
    const next = queue[1]
    if (!next) return
    const img = next.querySelector("img")
    if (!img || img.dataset.preloaded) return

    img.dataset.preloaded = "1"
    const warm = new Image()
    warm.src = img.currentSrc || img.src
  }

  // `skipped`: el producto se descarta sin contar ninguna de sus compañías.
  function finishCard(skipped = false) {
    const card = queue.shift()
    if (skipped) skippedProducts += 1
    else doneProducts += 1
    // `excludedIds` NO se depura: un producto ya procesado en esta sesión
    // (contado o saltado) no debe volver a aparecer si se pide "Contar más".

    if (queue.length > 0) {
      updateProgress()
      showCard(currentCard())
    } else {
      showEmptyState()
    }

    // La card vieja se saca DESPUÉS de haber enfocado el campo de la siguiente:
    // quitar del DOM el elemento enfocado lo desenfoca, y eso cierra el teclado.
    // Todo ocurre en el mismo bloque síncrono, así que nunca se pinta un cuadro
    // con las dos cards visibles.
    if (card) card.remove()
  }

  function showEmptyState() {
    const pendientes = Math.max(0, totalProducts - doneProducts - skippedProducts)

    saveBtn.hidden = true
    if (skipBtn) skipBtn.hidden = true
    if (finishBtn) finishBtn.hidden = true
    if (doneEl) doneEl.hidden = false
    // El header sólo aguanta el contador corto; el detalle (incluidos los
    // saltados y los que quedaron sin contar) va en el bloque de cierre, que sí
    // tiene ancho.
    if (progressLabel) progressLabel.textContent = `${doneProducts} de ${totalProducts}`
    const detail = document.querySelector("[data-done-detail]")
    if (detail) {
      const partes = []
      if (skippedProducts > 0) partes.push(`${skippedProducts} saltado${skippedProducts === 1 ? "" : "s"}`)
      if (pendientes > 0) partes.push(`${pendientes} sin contar`)
      const extra = partes.length > 0 ? ` (${partes.join(", ")})` : ""
      detail.textContent = `Contaste ${doneProducts} de ${totalProducts} productos${extra}.`
    }
    // El título miente si quedaron productos pendientes: se cerró el lote a
    // mano, la zona no está completa.
    const title = doneEl && doneEl.querySelector(".m-empty__title")
    if (title) title.textContent = pendientes > 0 ? "Lote terminado" : "¡Zona completa!"
    if (progressFill) progressFill.style.width = "100%"
  }

  // Cierra el lote sin contar lo que queda. No registra nada en el servidor:
  // esos productos siguen con `to_be_count: true` y vuelven en otra ronda.
  function finishBatch() {
    if (queue.length === 0) return

    queue.forEach((card) => card.remove())
    queue.length = 0
    showEmptyState()
  }

  function hideEmptyState() {
    saveBtn.hidden = false
    if (skipBtn) skipBtn.hidden = false
    if (finishBtn) finishBtn.hidden = false
    if (doneEl) doneEl.hidden = true
  }

  function switchCompany() {
    const card = currentCard()
    if (!card) return
    const otherStep = switchableStepsOf(card)[0]
    if (otherStep === undefined) return

    currentStep = otherStep
    renderStep(card)
  }

  // Saltar descarta el producto ENTERO (todas sus compañías) de esta sesión. No
  // registra nada en el servidor: el producto sigue con `to_be_count: true` y
  // vuelve a salir en una próxima ronda, para que lo cuente alguien más.
  function skipProduct() {
    if (!currentCard()) return

    toast("Producto saltado — queda pendiente de contar", "info")
    finishCard(true)
  }

  async function save() {
    const card = currentCard()
    if (!card || saveBtn.disabled || saveBtn.classList.contains("is-loading")) return

    const steps = stepsOf(card)
    const step = steps[currentStep]
    const qtyInput = card.querySelector("[data-count-qty]")
    const quantity = qtyInput ? qtyInput.value.trim() : ""
    if (!step || quantity === "") return

    saveBtn.classList.add("is-loading")
    saveBtn.disabled = true

    const { ok, data, networkError } = await post(card.dataset.countUrl, {
      product_id: card.dataset.productId,
      zone_id: card.dataset.zoneId,
      company: step.company,
      quantity,
    })

    saveBtn.classList.remove("is-loading")

    if (networkError) {
      toast("No se pudo guardar: revisá la señal y volvé a intentar. El valor no se perdió.", "error")
      saveBtn.disabled = false
      return
    }

    if (!ok && !data) {
      toast("No se pudo guardar. Intentá de nuevo.", "error")
      saveBtn.disabled = false
      return
    }

    const status = data?.status

    if (status === "error") {
      toast(data.message || "No se pudo guardar.", "error")
      saveBtn.disabled = false
      return
    }

    if (status === "big_difference" && data.registered === false) {
      // No se guardó a propósito (posible conteo malo): se deja el valor para
      // que la persona lo revise, no se avanza.
      toast(data.message, "error")
      saveBtn.disabled = false
      return
    }

    // "ok" o "already_counted" (o big_difference ya registrado): este paso queda
    // resuelto. Si al producto le queda el otro, se muestra; si no, se cierra.
    toast(data?.message || "Guardado ✓", status === "already_counted" ? "info" : data?.alert ? "warning" : "success")
    if (navigator.vibrate) navigator.vibrate(30)

    countedSteps.add(currentStep)

    // Sólo las compañías CON unidades mantienen abierto el producto. Un paso
    // `warn` contado a mano no obliga a recorrer el resto: si ya no queda
    // ninguna compañía con stock por contar, el producto se cierra.
    const pendientes = requiredStepsOf(card)
    if (pendientes.length === 0) {
      finishCard()
    } else {
      currentStep = pendientes[0]
      renderStep(card)
    }
  }

  async function loadMore() {
    if (!moreBtn) return
    moreBtn.classList.add("is-loading")
    moreBtn.disabled = true

    const { ok, data } = await get(`${screen.dataset.nearbyUrl}?exclude_ids=${excludedIds.join(",")}`)

    moreBtn.classList.remove("is-loading")
    moreBtn.disabled = false

    if (!ok || !data?.products?.length) {
      toast("No hay más productos para contar en esta zona por ahora.", "info")
      return
    }

    const hadNoCards = queue.length === 0

    data.products.forEach((entry) => {
      const wrapper = document.createElement("div")
      wrapper.innerHTML = entry.html.trim()
      const card = wrapper.firstElementChild
      if (!card) return
      queueEl.appendChild(card)
      queue.push(card)
      excludedIds.push(String(entry.id))
      totalProducts += 1
    })

    hideEmptyState()
    updateProgress()
    if (hadNoCards) showCard(currentCard())
  }

  queueEl.addEventListener("input", (event) => {
    if (event.target.matches("[data-count-qty]")) updateSaveButtonState()
  })

  queueEl.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && event.target.matches("[data-count-qty]")) {
      event.preventDefault()
      save()
    }
  })

  queueEl.addEventListener("click", (event) => {
    if (event.target.matches("[data-switch-company]")) switchCompany()
  })

  // Tocar un botón de la barra no debe robar el foco del campo: `preventDefault`
  // en `mousedown` (el evento en el que el navegador mueve el foco, también en
  // Android/iOS a través del mouse sintético) deja el teclado abierto durante
  // todo el guardado. El `click` se sigue disparando normal.
  ;[saveBtn, skipBtn].forEach((btn) => {
    if (btn) btn.addEventListener("mousedown", (event) => event.preventDefault())
  })

  saveBtn.addEventListener("click", save)
  if (skipBtn) skipBtn.addEventListener("click", skipProduct)
  if (finishBtn) finishBtn.addEventListener("click", finishBatch)
  if (moreBtn) moreBtn.addEventListener("click", loadMore)

  updateProgress()
  showCard(currentCard())
}
